// Logica pura della Timeline (Batch 2). Nessun side-effect, nessun accesso al
// DOM: opera solo sull'array di foto GIÀ caricato (stesso `photos` della mappa,
// già filtrato backend-side per privacy). Testabile in isolamento
// (scripts/test-timeline.mjs). Nessun fetch, nessun cambio di contratto API.

import type { PhotoSummary } from "./api";

export interface DayGroup {
  /** Chiave giorno locale "YYYY-MM-DD". */
  key: string;
  /** ISO rappresentativa del gruppo (data effettiva del primo item), per la formattazione. */
  dateIso: string;
  items: PhotoSummary[];
}

/** Data effettiva del ricordo: `taken_at` se presente, altrimenti `created_at`. */
export function effectiveDate(p: Pick<PhotoSummary, "taken_at" | "created_at">): string {
  return p.taken_at ?? p.created_at;
}

/** Timestamp (ms) della data effettiva; `-Infinity` se non parsabile (ordina in fondo). */
function effTime(p: PhotoSummary): number {
  const t = new Date(effectiveDate(p)).getTime();
  return Number.isNaN(t) ? -Infinity : t;
}

/** Chiave giorno locale "YYYY-MM-DD" da un ISO; `null` se la data non è valida. */
function dayKeyOf(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Raggruppa le foto per giorno (ora locale). Gruppi ordinati dal più recente al
 * più vecchio; item interni ordinati dal più recente. Gli item con data non
 * valida vengono esclusi dai gruppi giornalieri senza causare errori.
 */
export function groupByDay(photos: PhotoSummary[]): DayGroup[] {
  const map = new Map<string, PhotoSummary[]>();
  for (const p of photos) {
    const key = dayKeyOf(effectiveDate(p));
    if (!key) continue;
    const list = map.get(key);
    if (list) list.push(p);
    else map.set(key, [p]);
  }
  const groups: DayGroup[] = [];
  for (const [key, items] of map) {
    items.sort((a, b) => effTime(b) - effTime(a));
    groups.push({ key, dateIso: effectiveDate(items[0]), items });
  }
  groups.sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
  return groups;
}

/**
 * True se il ricordo cade nello stesso giorno/mese di `today` ma in un anno
 * PRECEDENTE (ora locale). I ricordi di oggi stesso non sono "in questo giorno".
 */
export function isOnThisDay(p: PhotoSummary, today: Date): boolean {
  const d = new Date(effectiveDate(p));
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate() &&
    d.getFullYear() < today.getFullYear()
  );
}

/** Ricordi "In questo giorno": stesso giorno/mese, anni precedenti, dal più recente. */
export function onThisDay(photos: PhotoSummary[], today: Date): PhotoSummary[] {
  return photos.filter((p) => isOnThisDay(p, today)).sort((a, b) => effTime(b) - effTime(a));
}
